import { cn } from "@/lib/utils";
import { clsx } from "clsx";
import { useCallback, useEffect, useState } from "react";
import { isSafari } from "react-device-detect";
import { Oval } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { NavLink, useSearchParams } from "react-router-dom";

import { Button } from "../../../components/atoms/Buttons/Button";
import {
  checkMarkIcon,
  todoIcon,
  warningIcon,
} from "../../../designSystemIcons";
import { isChromeVersionUpToDate } from "../../../functions/isChromeVersionUpToDate";
import { sendMessageToExtension } from "../../../functions/sendMessageToExtension";
import { useInitImport } from "../../../hooks/useInitImport";
import { setIsCurrentlyImporting } from "../../../redux/globalState/globalStateSlice";
import { addAlert, showModal } from "../../../redux/message/messageSlice";
import { AppDispatch } from "../../../redux/store";
import {
  fetchPlayerCount,
  fetchUserNotifications,
  getUserSelector,
} from "../../../redux/user/userSlice";

const errorTypes = [
  "notUsingMobileSafari",
  "noExtension",
  "noSubscription",
  "outdatedExtension",
  "noEaExtensionAccess",
] as const;

type Error = (typeof errorTypes)[number];

const errorTexts: Record<(typeof errorTypes)[number], React.ReactNode> = {
  notUsingMobileSafari:
    "You need to use Safari on your mobile to import players with our Safari Extension.",
  noExtension:
    "You need to install and allow our Safari Extension to import players.",
  noEaExtensionAccess: (
    <div className="flex gap-x-2 ml-8 mb-2">
      <div className="w-5 h-5 my-auto shrink-0">{warningIcon}</div>
      <p className="text-sm text-dark-tertiary">
        You need to allow access for the extension on ea.com. (Enable access
        through Safari Extension settings)
      </p>
    </div>
  ),
  noSubscription: <div></div>,
  outdatedExtension:
    "Your extension is outdated. To update it completely close and reopen Safari.",
} as const;

export const ImportPlayersViewSafari = () => {
  const { user } = useSelector(getUserSelector);
  const [importing, setImporting] = useState(false);
  const [extensionVersion, setExtensionVersion] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const initImport = useInitImport({ user });
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const from = searchParams.get("from");
  const [errors, setErrors] = useState<Record<Error, boolean>>({
    notUsingMobileSafari: false,
    noExtension: false,
    noSubscription: !localStorage.getItem("canUseClubImport"),
    outdatedExtension: false,
    noEaExtensionAccess: false,
  });
  const [isOldIOS, setIsOldIOS] = useState(false);

  useEffect(() => {
    const userAgent = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      const match = userAgent.match(/OS (\d+)[._](\d+)?[._]?(\d+)?/);
      if (match) {
        const major = parseInt(match[1], 10);
        const minor = parseInt(match[2] || "0", 10);
        const patch = parseInt(match[3] || "0", 10);
        // check if major version is less than 18:
        const isOldIOS = major < 18;
        setIsOldIOS(isOldIOS);
      }
    }
  }, [navigator.userAgent]);

  useEffect(() => {
    if (from === "extension-app") {
      localStorage.setItem("FROM_EXTENSION_APP", "true");
      setSearchParams({});
    }
  }, [from]);
  useEffect(() => {
    sendMessageToExtension({ user, safariMobile: true })
      .then((res) => {
        setErrors((prev) => ({
          ...prev,
          noExtension: res.msg !== "confirmation",
          outdatedExtension: !isChromeVersionUpToDate([2, 1, 11], res.version),
          noEaExtensionAccess: !res.hasEaAccess,
        }));
        setExtensionVersion(res.version);
      })
      .catch(() => setErrors((prev) => ({ ...prev, noExtension: true })));
  }, [user]);

  const handleSendMessage = useCallback(() => {
    if (!user || importing) return;

    sendMessageToExtension({ user, safariMobile: true })
      .then((res) => {
        setErrors((prev) => ({
          ...prev,
          noExtension: res.msg !== "confirmation",
          noEaExtensionAccess: !res.hasEaAccess,
          outdatedExtension: !isChromeVersionUpToDate([2, 1, 11], res.version),
        }));
        setExtensionVersion(res.version);
      })
      .catch(() => setErrors((prev) => ({ ...prev, noExtension: true })));
  }, [user, importing]);

  useEffect(() => {
    const intervalId = setInterval(handleSendMessage, 1000);
    return () => clearInterval(intervalId);
  }, [handleSendMessage]);

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      noSubscription: !localStorage.getItem("canUseClubImport"),
      notUsingMobileSafari: !isSafari,
      outdatedExtension:
        (!window?.chrome && !isSafari) ||
        !extensionVersion ||
        !isChromeVersionUpToDate([2, 1, 11], extensionVersion),
    }));
  }, [user?.subscription, extensionVersion]);

  return (
    <main
      className="flex min-h-screen pt-4 mx-auto text-white touch-device:pb-8 touch-device:pt-8"
      style={{
        background:
          "linear-gradient(180deg, #1C2A37 0%, #161C21 100%), #1D1D1D",
      }}
    >
      <div className="flex flex-col mx-auto text-base gap-y-12 md-sm:text-sm touch-device:gap-y-16 touch-device:text-base p-4">
        <div className="flex flex-col gap-y-4">
          <h2 className="text-2xl font-medium">Mobile Club Import</h2>
          <div className="flex flex-col gap-y-5">
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <div className="flex gap-x-3">
                  <div className="my-auto w-5 h-5">
                    {errors.notUsingMobileSafari ? todoIcon : checkMarkIcon}
                  </div>
                  <p
                    className={clsx(
                      !errors.notUsingMobileSafari && "text-dark-tertiary",
                    )}
                  >
                    Use Safari
                  </p>
                </div>
              </div>
              {
                <p className="text-[#F94D6A] pl-8 text-sm">
                  {errors.notUsingMobileSafari &&
                    errorTexts.notUsingMobileSafari}
                </p>
              }
            </div>
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <div className="flex gap-x-3">
                  <div className=" my-auto w-5 h-5 shrink-0">
                    {errors.noSubscription ? todoIcon : checkMarkIcon}
                  </div>
                  <p
                    className={clsx(
                      (!errors.noSubscription || errors.notUsingMobileSafari) &&
                        "text-dark-tertiary",
                    )}
                  >
                    Log in with an account with an active Elite or Ultimate
                    subscription.
                  </p>
                </div>
              </div>
              {errors.noSubscription && !errors.notUsingMobileSafari && (
                <p className="text-[#F94D6A] touch-device:pl-4">
                  <div className="flex flex-col">
                    <p>
                      You need an Elite or Ultimate subscription to import
                      players.
                    </p>
                    <div className="flex gap-x-2">
                      <NavLink
                        to={"/subscription"}
                        className="underline text-primary-blue"
                      >
                        See Subscriptions here
                      </NavLink>
                      or
                      <button
                        className="underline text-primary-blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(
                            showModal({
                              modalType: "login-required",
                              premiumFeatureHook: "import-club",
                            }),
                          );
                        }}
                      >
                        Login here
                      </button>
                    </div>
                  </div>
                </p>
              )}
            </div>
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <div className="flex gap-x-3 mb-3">
                  <div className=" my-auto w-5 h-5">
                    {errors.noExtension ? todoIcon : checkMarkIcon}
                  </div>
                  <span
                    className={clsx(
                      (!errors.noExtension ||
                        errors.noSubscription ||
                        errors.notUsingMobileSafari) &&
                        "text-dark-tertiary",
                    )}
                  >
                    Setup the extension
                  </span>
                </div>
              </div>

              {errors.noExtension &&
                !errors.noSubscription &&
                !errors.notUsingMobileSafari && (
                  <div className="-mx-2 mb-3">
                    <img
                      src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/mobile-extension-guide${isOldIOS ? "-old-ios" : ""}.png`}
                      alt="Mobile extension guide"
                      className="w-full"
                    />
                  </div>
                )}
              {errors.noExtension &&
              !errors.noSubscription &&
              !errors.notUsingMobileSafari ? (
                <p className={clsx("text-[#F94D6A] text-sm pl-8 mb-2")}>
                  {errorTexts.noExtension}
                </p>
              ) : (
                errors.noEaExtensionAccess && errorTexts.noEaExtensionAccess
              )}

              <div className="flex gap-x-1">
                <div className="flex gap-x-3 mb-3">
                  <div className="my-auto w-5 h-5">{todoIcon}</div>
                  <span
                    className={clsx(
                      (errors.noExtension ||
                        errors.noSubscription ||
                        errors.notUsingMobileSafari) &&
                        "text-dark-tertiary",
                    )}
                  >
                    {"Import your players " +
                      (!errors.noExtension &&
                      !errors.noSubscription &&
                      !errors.notUsingMobileSafari
                        ? "(follow the steps below)"
                        : "")}
                  </span>
                </div>
              </div>
            </div>
            {!errors.noSubscription &&
              !errors.noExtension &&
              !errors.notUsingMobileSafari && (
                <>
                  <div className="full-vw overflow-x-scroll flex">
                    <img
                      src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/${errors.noEaExtensionAccess ? (isOldIOS ? "import-guide-with-allow-ea-old-ios" : "import-guide-with-allow-ea") : "yoyo"}.png`}
                      className={cn(
                        errors.noEaExtensionAccess
                          ? "min-w-[1800px]"
                          : "min-w-[1200px]",
                      )}
                    />
                  </div>
                  <p className="text-sm italic text-dark-tertiary">
                    {`Scroll right to see more steps ${"-->"}`}
                  </p>
                </>
              )}
            <div className="flex flex-col mt-8 ">
              <div className="flex gap-x-1">
                <span className="text-sm text-dark-tertiary">
                  Still having issues? Contact us on our{" "}
                  <a
                    href="https://discord.gg/easysbc-980827940902293525"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-primary-blue"
                  >
                    Discord server
                  </a>{" "}
                  to get help.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex z-20 justify-center py-4 mt-4 bg-[#282D34] gap-x-2 fixed bottom-13 right-0 w-full touch-device:mx-4">
        <div className="w-[150px]">
          <Button
            variant="tertiary"
            size="large"
            disabled={
              errors.notUsingMobileSafari ||
              errors.noExtension ||
              errors.noSubscription ||
              importing
            }
            disabledVariant="transparent"
            onClick={() => {
              setImporting(true);
              window.open(
                "https://www.ea.com/ea-sports-fc/ultimate-team/web-app/",
                "_blank",
              );
              initImport().then(() => {});
            }}
          >
            Start Import
          </Button>
        </div>
        <div className="w-[150px]">
          <Button
            variant="tertiary"
            size="large"
            disabled={!importing}
            disabledVariant="transparent"
            onClick={() => {
              dispatch(setIsCurrentlyImporting(false));
              setLoading(true);
              sendMessageToExtension({
                user,
                doneRequest: true,
                safariMobile: true,
              })
                .then(() => {
                  dispatch(fetchUserNotifications());
                  dispatch(fetchPlayerCount());
                  setImporting(false);
                  setLoading(false);
                  navigate("/my-club");
                })
                .catch(() =>
                  dispatch(
                    addAlert({
                      type: "error",
                      title: "Club import",
                      description: "Couldn't import players",
                    }),
                  ),
                );
            }}
          >
            {loading ? <Oval height={"24px"} width={"24px"} /> : "Done"}
          </Button>
        </div>
      </div>
    </main>
  );
};
